import { useState } from 'react';
import { FilePlus2Icon, RadioIcon, ShieldXIcon } from 'lucide-react';
import type { IncidentSignalWeergave } from '@kennisbank/shared';
import { ApiError, apiPost } from '../../api/client.js';
import { SIGNAAL_TITEL, SIGNAAL_UITLEG } from './labels.js';
import { formatMoment } from './termijn.js';

/**
 * Signaalkaarten uit `security_incident_signals`: patronen die de janitor in
 * de audit log vond voordat iemand handmatig meldde (plan §7a).
 *
 * Twee acties, en de tweede is net zo belangrijk als de eerste: een
 * gemotiveerde "geen incident" is aantoonbare opvolging. Daarom is de
 * motivering verplicht, precies zoals `ReviewIncidentSignalInput` afdwingt.
 */
export function SignaalKaarten({
  signals,
  onReviewed,
}: {
  signals: IncidentSignalWeergave[];
  onReviewed: (nieuwIncidentId?: string) => void;
}): JSX.Element | null {
  const open = signals.filter((s) => s.status === 'open');
  if (open.length === 0) return null;

  return (
    <section className="mb-6">
      <h2 className="mb-2 flex items-center gap-2 text-base font-bold text-ink-900">
        <RadioIcon size={18} className="text-brand-700" />
        Signalen uit de audit log
        <span className="rounded-sm bg-brand-50 px-2 py-0.5 text-[11px] font-semibold text-brand-700">
          {open.length}
        </span>
      </h2>
      <p className="mb-3 text-sm text-ink-900/65">
        Patronen die het systeem zelf opmerkte. Beoordeel elk signaal: maak er een melding van, of
        leg vast waarom het er geen is.
      </p>
      <ul className="flex flex-col gap-3">
        {open.map((s) => (
          <SignaalKaart key={s.id} signaal={s} onReviewed={onReviewed} />
        ))}
      </ul>
    </section>
  );
}

function SignaalKaart({
  signaal,
  onReviewed,
}: {
  signaal: IncidentSignalWeergave;
  onReviewed: (nieuwIncidentId?: string) => void;
}): JSX.Element {
  const [motivering, setMotivering] = useState('');
  const [afwijzen, setAfwijzen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function review(action: 'maak_melding' | 'geen_incident'): Promise<void> {
    setBusy(true);
    setErr(null);
    try {
      const resp = await apiPost<{ incidentId?: string; incident?: { id?: string } } | null>(
        `/api/v1/beheer/incident-signals/${signaal.id}`,
        {
          action,
          ...(action === 'geen_incident' ? { motivering: motivering.trim() } : {}),
        },
      );
      onReviewed(resp?.incidentId ?? resp?.incident?.id);
    } catch (e) {
      setErr(e instanceof ApiError ? `Mislukt (${e.status}): ${e.message}` : 'Mislukt.');
      setBusy(false);
    }
  }

  return (
    <li className="rounded-2xl border border-ring1-border bg-mosterdgeel-10 p-4 shadow-card">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="font-semibold text-ink-900">{SIGNAAL_TITEL[signaal.rule]}</p>
        <span className="rounded-sm bg-white px-2 py-0.5 text-[11px] font-semibold text-ink-900/70 tabular-nums">
          {signaal.aantal}× in deze periode
        </span>
      </div>
      <p className="mt-1 text-sm text-ink-900/75">{SIGNAAL_UITLEG[signaal.rule]}</p>
      <dl className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-[12px] text-ink-900/65">
        <div>
          <dt className="inline font-medium">Tijdvenster: </dt>
          <dd className="inline tabular-nums">
            {formatMoment(signaal.vensterVan)} tot {formatMoment(signaal.vensterTot)}
          </dd>
        </div>
        {signaal.actorUserId && (
          <div>
            <dt className="inline font-medium">Gebruiker: </dt>
            <dd className="inline" title={signaal.actorUserId}>
              {signaal.gebruiker?.naam ?? signaal.actorUserId}
              {signaal.gebruiker?.email && signaal.gebruiker.email !== signaal.gebruiker.naam
                ? ` (${signaal.gebruiker.email})`
                : ''}
            </dd>
          </div>
        )}
        {!signaal.actorUserId && signaal.actorEntraOid && (
          <div>
            <dt className="inline font-medium">Entra-account: </dt>
            <dd className="inline">{signaal.actorEntraOid}</dd>
          </div>
        )}
        <div>
          <dt className="inline font-medium">Logregels: </dt>
          <dd className="inline tabular-nums">{signaal.auditLogIds.length}</dd>
        </div>
      </dl>
      {signaal.documenten && (
        <details className="mt-2 text-[12px] text-ink-900/75">
          <summary className="cursor-pointer">
            {signaal.documenten.opvragingen} opvragingen, {signaal.documenten.verschillend}{' '}
            verschillende documenten: {signaal.documenten.bekeken}× bekeken,{' '}
            {signaal.documenten.gedownload}× gedownload
          </summary>
          <ul className="mt-1 list-disc pl-5">
            {signaal.documenten.titels.map((t, i) => (
              <li key={i}>{t}</li>
            ))}
          </ul>
        </details>
      )}

      {afwijzen && (
        <div className="mt-3 rounded-md bg-white p-3">
          <label
            className="block text-[12px] font-medium text-ink-900/70"
            htmlFor={`mot-${signaal.id}`}
          >
            Waarom is dit geen incident? Deze motivering wordt vastgelegd.
          </label>
          <textarea
            id={`mot-${signaal.id}`}
            value={motivering}
            onChange={(e) => setMotivering(e.target.value)}
            rows={2}
            className="mt-1 w-full rounded-md border border-ring2 px-3 py-1.5 text-sm text-ink-900"
            placeholder="Bijvoorbeeld: gebruiker klikte een oude link uit een e-mail, geen toegang verkregen."
          />
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={() => void review('maak_melding')}
          className="inline-flex items-center gap-1.5 rounded-lg bg-brand-500 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
        >
          <FilePlus2Icon size={14} /> Maak melding aan
        </button>
        {afwijzen ? (
          <>
            <button
              type="button"
              disabled={busy || motivering.trim().length === 0}
              onClick={() => void review('geen_incident')}
              title={
                motivering.trim().length === 0
                  ? 'Geef eerst een motivering'
                  : 'Legt de afwijzing met motivering vast'
              }
              className="inline-flex items-center gap-1.5 rounded-lg bg-ink-900 px-3 py-1.5 text-sm font-semibold text-white hover:bg-ink-700 disabled:opacity-40"
            >
              <ShieldXIcon size={14} /> Vastleggen als geen incident
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setAfwijzen(false);
                setMotivering('');
              }}
              className="rounded-lg border border-ring2 bg-white px-3 py-1.5 text-sm font-medium text-ink-900 hover:bg-brand-50"
            >
              Annuleer
            </button>
          </>
        ) : (
          <button
            type="button"
            disabled={busy}
            onClick={() => setAfwijzen(true)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-ring2 bg-white px-3 py-1.5 text-sm font-medium text-ink-900 hover:bg-brand-50 disabled:opacity-50"
          >
            <ShieldXIcon size={14} /> Geen incident
          </button>
        )}
        {err && <span className="text-[12px] text-error">{err}</span>}
      </div>
    </li>
  );
}
